// Family chart layout: a layered ("Sugiyama-lite") layout around a focus person.
//
// 1. Select who to show: ancestors up to N generations, descendants down to M, optionally
//    siblings/cousins, and partners/co-parents.
// 2. Assign each person a generation (row). Partners share a row.
// 3. Group partners in the same row into "units" (couples, polycules) that stay adjacent.
// 4. Order units in each row by the barycenter of their relatives in neighboring rows.
// 5. Assign x positions: each unit wants to sit centered over/under its relatives; overlaps are
//    resolved with isotonic regression (pool-adjacent-violators), which keeps the order and
//    minimum spacing while staying as close as possible to the desired positions.
//
// Works for any graph shape: multiple parents, same-gender couples, polyamory, cousin marriage.

import { graphOf, other, parentsOf, childrenOf, partnersOf } from '../../model/graph';
import type { Id, ParentType, PartnerRel, Tree } from '../../model/types';

export const CARD_W = 184;
export const CARD_H = 66;
export const PARTNER_GAP = 28;
export const UNIT_GAP = 30;
export const ROW_GAP = 96;
export const ROW_H = CARD_H + ROW_GAP;

export type Collateral = 'direct' | 'siblings' | 'all';

export interface LayoutOptions {
  ancestors: number;
  descendants: number;
  collateral: Collateral;
  partners: boolean;
  /** Safety cap; selection stops growing past this many people. */
  maxPeople: number;
  /**
   * Everyone linked to the focus through parents, children, partners, and siblings, however
   * far (the whole family, for exports), instead of the generation limits above.
   */
  whole?: boolean;
  /** Card height; taller when cards show more details (cards.ts). */
  cardH?: number;
}

export interface LayoutNode {
  id: Id;
  x: number; // center
  y: number; // top
  gen: number;
  hiddenParents: boolean;
  hiddenChildren: boolean;
}

export interface PartnerEdge {
  rel: PartnerRel;
  a: Id;
  b: Id;
  adjacent: boolean;
}

export interface FamilyEdge {
  parents: Id[];
  children: { id: Id; types: ParentType[] }[];
  /** Regular = parents all one row above all children; drawn as a bus. */
  regular: boolean;
  busY: number;
}

export interface Layout {
  nodes: Map<Id, LayoutNode>;
  partners: PartnerEdge[];
  families: FamilyEdge[];
  bounds: { minX: number; maxX: number; minY: number; maxY: number };
  truncated: boolean;
  /** Height of every card in this layout. */
  cardH: number;
}

export function layoutChart(tree: Tree, focus: Id, opts: LayoutOptions): Layout {
  const g = graphOf(tree);
  const cardH = opts.cardH ?? CARD_H;
  const rowH = cardH + ROW_GAP;
  const gen = new Map<Id, number>();
  let truncated = false;
  const add = (id: Id, gn: number) => {
    if (gen.has(id)) return false;
    if (gen.size >= opts.maxPeople) {
      truncated = true;
      return false;
    }
    gen.set(id, gn);
    return true;
  };

  // ---- 1. selection
  add(focus, 0);
  const ancestors: Id[] = [focus];
  if (opts.whole) {
    // Breadth first, so each person's row comes from their closest link to the focus.
    const queue = [focus];
    for (let i = 0; i < queue.length; i++) {
      const x = queue[i];
      const gx = gen.get(x)!;
      for (const p of partnersOf(g, x)) if (add(p, gx)) queue.push(p);
      for (const p of parentsOf(g, x)) if (add(p, gx - 1)) queue.push(p);
      for (const c of childrenOf(g, x)) if (add(c, gx + 1)) queue.push(c);
      for (const r of g.siblingRels.get(x) ?? []) if (add(other(r, x), gx)) queue.push(other(r, x));
    }
  } else {
    let frontier = [focus];
    for (let d = 1; d <= opts.ancestors && frontier.length; d++) {
      const next: Id[] = [];
      for (const x of frontier) for (const p of parentsOf(g, x)) if (add(p, -d)) { next.push(p); ancestors.push(p); }
      frontier = next;
    }
    const descend = (root: Id, maxGen: number) => {
      let fr = [root];
      while (fr.length) {
        const next: Id[] = [];
        for (const x of fr) {
          const gx = gen.get(x)!;
          if (gx >= maxGen) continue;
          for (const c of childrenOf(g, x)) if (add(c, gx + 1)) next.push(c);
        }
        fr = next;
      }
    };
    descend(focus, opts.descendants);
    if (opts.collateral === 'siblings') {
      for (const a of ancestors) for (const p of parentsOf(g, a)) if (gen.has(p)) for (const c of childrenOf(g, p)) add(c, gen.get(p)! + 1);
    } else if (opts.collateral === 'all') {
      // Cousins: every descendant of every shown ancestor, down to the descendant limit.
      for (const a of ancestors) if (a !== focus) descend(a, Math.max(opts.descendants, 0));
    }
  }
  if (opts.partners) {
    for (const id of [...gen.keys()]) for (const p of partnersOf(g, id)) add(p, gen.get(id)!);
    // Co-parents of shown children (people who had a child together without a recorded partnership).
    const ancestorSet = new Set(ancestors);
    for (const id of [...gen.keys()]) {
      const gi = gen.get(id)!;
      if (id === focus || ancestorSet.has(id)) continue;
      // Only complete families that are already partly shown, so partners' own parents don't sprawl in.
      const ps = parentsOf(g, id);
      if (!ps.some((p) => gen.has(p))) continue;
      for (const p of ps) add(p, gi - 1);
    }
  }

  const ids = [...gen.keys()];
  const shown = new Set(ids);

  // ---- 2/3. rows and units
  const rows = new Map<number, Id[]>();
  for (const id of ids) {
    const r = gen.get(id)!;
    if (!rows.has(r)) rows.set(r, []);
    rows.get(r)!.push(id);
  }
  const rowKeys = [...rows.keys()].sort((a, b) => a - b);

  type Unit = { members: Id[]; x: number; row: number };
  const unitOf = new Map<Id, Unit>();
  const rowUnits = new Map<number, Unit[]>();
  const partnerAdj = (id: Id) => partnersOf(g, id).filter((p) => shown.has(p) && gen.get(p) === gen.get(id));

  for (const r of rowKeys) {
    const list: Unit[] = [];
    const seen = new Set<Id>();
    for (const id of rows.get(r)!) {
      if (seen.has(id)) continue;
      // Collect the partner component.
      const comp: Id[] = [];
      const stack = [id];
      seen.add(id);
      while (stack.length) {
        const x = stack.pop()!;
        comp.push(x);
        for (const p of partnerAdj(x)) if (!seen.has(p)) { seen.add(p); stack.push(p); }
      }
      // Order: hub in the middle, partners alternating left/right in BFS order.
      const hub = comp.reduce((best, x) => (partnerAdj(x).length > partnerAdj(best).length ? x : best), comp[0]);
      const order: Id[] = [hub];
      const placed = new Set([hub]);
      const q = [hub];
      let left = true;
      while (q.length) {
        const x = q.shift()!;
        for (const p of partnerAdj(x)) {
          if (placed.has(p)) continue;
          placed.add(p);
          q.push(p);
          // A neighbor of an end member goes on that end, which keeps chains (A–B–C) straight.
          if (order[0] === x) order.unshift(p);
          else if (order[order.length - 1] === x) order.push(p);
          else if (left) order.unshift(p);
          else order.push(p);
          left = !left;
        }
      }
      const u: Unit = { members: order, x: 0, row: r };
      for (const m of order) unitOf.set(m, u);
      list.push(u);
    }
    rowUnits.set(r, list);
  }

  const unitWidth = (u: Unit) => u.members.length * CARD_W + (u.members.length - 1) * PARTNER_GAP;
  const memberOffset = (u: Unit, id: Id) => {
    const i = u.members.indexOf(id);
    return -unitWidth(u) / 2 + CARD_W / 2 + i * (CARD_W + PARTNER_GAP);
  };
  const posOf = (id: Id) => {
    const u = unitOf.get(id)!;
    return u.x + memberOffset(u, id);
  };

  // Neighbors in the chart, in adjacent rows only (parents above, children below).
  const upN = new Map<Id, Id[]>();
  const downN = new Map<Id, Id[]>();
  for (const id of ids) {
    upN.set(id, parentsOf(g, id).filter((p) => shown.has(p) && gen.get(p)! < gen.get(id)!));
    downN.set(id, childrenOf(g, id).filter((c) => shown.has(c) && gen.get(c)! > gen.get(id)!));
  }

  // ---- 4. ordering
  const pack = (units: Unit[]) => {
    let x = 0;
    for (const u of units) {
      const w = unitWidth(u);
      u.x = x + w / 2;
      x += w + UNIT_GAP;
    }
  };
  // Initial order: by discovery order, which follows family lines.
  for (const r of rowKeys) pack(rowUnits.get(r)!);
  const bary = (u: Unit, dir: 'up' | 'down' | 'both') => {
    const xs: number[] = [];
    for (const m of u.members) {
      if (dir !== 'down') for (const p of upN.get(m)!) xs.push(posOf(p));
      if (dir !== 'up') for (const c of downN.get(m)!) xs.push(posOf(c));
    }
    return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : u.x;
  };
  const focusRow = 0;
  const upRows = rowKeys.filter((r) => r < focusRow).reverse();
  const downRows = rowKeys.filter((r) => r > focusRow);
  const sortRow = (r: number, dir: 'up' | 'down' | 'both') => {
    const units = rowUnits.get(r)!;
    // Two-person units: put each person on the side of their own relatives.
    for (const u of units) {
      if (u.members.length !== 2) continue;
      const [a, b] = u.members;
      const ka = upN.get(a)!.length ? upN.get(a)!.map(posOf).reduce((s, v) => s + v, 0) / upN.get(a)!.length : undefined;
      const kb = upN.get(b)!.length ? upN.get(b)!.map(posOf).reduce((s, v) => s + v, 0) / upN.get(b)!.length : undefined;
      if (ka !== undefined && kb !== undefined && ka > kb) u.members.reverse();
      else if (ka === undefined && kb !== undefined && dir !== 'up') {
        // a married in; b's family is above — put b toward its parents relative to unit center.
        if (kb < u.x) u.members.reverse();
      } else if (kb === undefined && ka !== undefined && ka > u.x) u.members.reverse();
    }
    const keyed = units.map((u, i) => ({ u, k: bary(u, dir), i }));
    keyed.sort((a, b) => a.k - b.k || a.i - b.i);
    rowUnits.set(r, keyed.map((x) => x.u));
    pack(rowUnits.get(r)!);
  };
  for (let iter = 0; iter < 3; iter++) {
    for (const r of upRows) sortRow(r, 'down');
    for (const r of downRows) sortRow(r, 'up');
    for (const r of downRows.slice().reverse()) sortRow(r, 'down');
    for (const r of upRows.slice().reverse()) sortRow(r, 'both');
  }

  // ---- 5. x positions
  const place = (r: number, dir: 'up' | 'down' | 'both') => {
    const units = rowUnits.get(r)!;
    if (!units.length) return;
    const desired = units.map((u) => {
      const vals: number[] = [];
      for (const m of u.members) {
        const off = memberOffset(u, m);
        if (dir !== 'down') for (const p of upN.get(m)!) vals.push(posOf(p) - off);
        if (dir !== 'up') for (const c of downN.get(m)!) vals.push(posOf(c) - off);
      }
      return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : u.x;
    });
    // Isotonic regression: minimize Σ(x_i - d_i)² s.t. x_{i+1} ≥ x_i + sep_i.
    const offs: number[] = [0];
    for (let i = 1; i < units.length; i++) offs.push(offs[i - 1] + unitWidth(units[i - 1]) / 2 + UNIT_GAP + unitWidth(units[i]) / 2);
    const target = desired.map((d, i) => d - offs[i]);
    const blocks: { sum: number; n: number; start: number }[] = [];
    for (let i = 0; i < target.length; i++) {
      blocks.push({ sum: target[i], n: 1, start: i });
      while (blocks.length > 1 && blocks[blocks.length - 2].sum / blocks[blocks.length - 2].n > blocks[blocks.length - 1].sum / blocks[blocks.length - 1].n) {
        const b = blocks.pop()!;
        const a = blocks[blocks.length - 1];
        a.sum += b.sum;
        a.n += b.n;
      }
    }
    for (const b of blocks) {
      const v = b.sum / b.n;
      for (let i = b.start; i < b.start + b.n; i++) units[i].x = v + offs[i];
    }
  };
  for (let iter = 0; iter < 12; iter++) {
    for (const r of downRows) place(r, 'up');
    for (const r of upRows) place(r, 'down');
    for (const r of rowKeys) place(r, 'both');
  }

  // ---- output
  const focusX = posOf(focus);
  const nodes = new Map<Id, LayoutNode>();
  for (const id of ids) {
    const r = gen.get(id)!;
    nodes.set(id, {
      id,
      x: posOf(id) - focusX,
      y: r * rowH,
      gen: r,
      hiddenParents: parentsOf(g, id).some((p) => !shown.has(p)),
      hiddenChildren: childrenOf(g, id).some((c) => !shown.has(c)),
    });
  }

  const partners: PartnerEdge[] = [];
  const seenRel = new Set<Id>();
  for (const id of ids)
    for (const r of g.partnerRels.get(id) ?? []) {
      const o = other(r, id);
      if (!shown.has(o) || seenRel.has(r.id)) continue;
      seenRel.add(r.id);
      const ua = unitOf.get(id)!;
      const ub = unitOf.get(o)!;
      const adjacent = ua === ub && Math.abs(ua.members.indexOf(id) - ua.members.indexOf(o)) === 1;
      partners.push({ rel: r, a: id, b: o, adjacent });
    }

  // Families: group shown children by their set of shown parents.
  const famMap = new Map<string, FamilyEdge>();
  for (const id of ids) {
    const prels = (g.parentRels.get(id) ?? []).filter((r) => shown.has(r.a));
    if (!prels.length) continue;
    const ps = prels.map((r) => r.a).sort();
    const key = ps.join('|');
    let fam = famMap.get(key);
    if (!fam) {
      fam = { parents: ps, children: [], regular: true, busY: 0 };
      famMap.set(key, fam);
    }
    fam.children.push({ id, types: prels.map((r) => r.type) });
  }
  const families = [...famMap.values()];
  // Bus heights: stagger families that share a row so their horizontal bars don't merge.
  const perRow = new Map<number, FamilyEdge[]>();
  for (const f of families) {
    const pg = f.parents.map((p) => gen.get(p)!);
    const cg = f.children.map((c) => gen.get(c.id)!);
    const prow = Math.max(...pg);
    f.regular = pg.every((x) => x === prow) && cg.every((x) => x === prow + 1);
    if (!f.regular) continue;
    if (!perRow.has(prow)) perRow.set(prow, []);
    perRow.get(prow)!.push(f);
  }
  for (const [prow, fams] of perRow) {
    const baseY = nodes.get(fams[0].parents[0])!.y + cardH;
    fams.sort((a, b) => famCenter(a) - famCenter(b));
    // Only overlapping families need different heights.
    const lanes: number[] = [];
    for (const f of fams) {
      const xs = [...f.parents.map((p) => nodes.get(p)!.x), ...f.children.map((c) => nodes.get(c.id)!.x)];
      const lo = Math.min(...xs);
      const hi = Math.max(...xs);
      let lane = lanes.findIndex((end) => end < lo - 8);
      if (lane === -1) {
        lane = lanes.length;
        lanes.push(hi);
      } else lanes[lane] = hi;
      f.busY = baseY + ROW_GAP / 2 + (lane % 2 ? 1 : -1) * Math.ceil(lane / 2) * 10;
    }
    void prow;
  }
  function famCenter(f: FamilyEdge) {
    return f.parents.reduce((s, p) => s + nodes.get(p)!.x, 0) / f.parents.length;
  }

  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const n of nodes.values()) {
    minX = Math.min(minX, n.x - CARD_W / 2);
    maxX = Math.max(maxX, n.x + CARD_W / 2);
    minY = Math.min(minY, n.y);
    maxY = Math.max(maxY, n.y + cardH);
  }
  return { nodes, partners, families, bounds: { minX, maxX, minY, maxY }, truncated, cardH };
}
